/**
 * The last screen before a white one. ADR-0008.
 *
 * The old app has no error boundary: an exception anywhere blanks it, inside Telegram,
 * with no way for a singer to report anything but "it broke". This is an addition, and
 * additions to a rebuild need a decision — FND-009.
 *
 * **The copy here is the only user-visible string in the app that is not verbatim from
 * the old one, and it is pending the human's approval.** It is modelled on the shape of
 * the login failure (`legacy/frontend/src/App.tsx:402-420`): say what happened in three
 * words, say what to do next, and offer the one action that might work. It addresses one
 * person as «ты», like everything else in this product, and it does not apologise.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { WarningIcon } from '../icons';
import { capture } from '../tracking';

type Props = { children: ReactNode };
type State = { failed: boolean };

export default class Boundary extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The component stack is the whole value of catching here rather than at
    // `window.error`: it says which screen died, which the message alone does not.
    capture(error, { kind: 'boundary', component_stack: info.componentStack ?? '' });
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <main className="shell">
        <section className="card">
          <p className="notice notice--danger">
            <WarningIcon size={18} />
            Что-то сломалось
          </p>
          <p className="failure">Попробуй открыть заново. Если не пройдёт — скажи регенту.</p>
          {/* A reload and not a state reset: whatever put the app here is in memory, and
              the screen that died is the one this would put back. */}
          <button className="btn btn--outline" onClick={() => window.location.reload()}>
            Попробовать снова
          </button>
        </section>
      </main>
    );
  }
}
